import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore';
import {
  ArrowTopRightOnSquareIcon,
  XMarkIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';
import { FaWhatsapp, FaFacebookMessenger } from 'react-icons/fa';
import { db } from '../firebase';
import {
  listAdminUnreadConversations,
  markConversationMessagesRead,
} from '../api/backend';
import { useAuth } from './AuthContext';

const POLL_INTERVAL_MS = 12000;

// Un solo panel para las dos mensajerías que tiene el sistema: el chat del
// portal de clientes (`conversations`) y el inbox de canales externos
// (`messaging_conversations`: WhatsApp, Messenger, Email). Antes esta burbuja
// solo mostraba la primera — así que si un cliente escribía por WhatsApp,
// nunca aparecía aquí y solo se veía entrando a /admin/whatsapp a mano.
const CHANNEL_META = {
  portal: { label: 'Portal', className: 'bg-violet-100 text-violet-700', Icon: ChatBubbleOvalLeftEllipsisIcon },
  whatsapp: { label: 'WhatsApp', className: 'bg-emerald-100 text-emerald-700', Icon: FaWhatsapp },
  messenger: { label: 'Messenger', className: 'bg-blue-100 text-blue-700', Icon: FaFacebookMessenger },
  email: { label: 'Email', className: 'bg-sky-100 text-sky-700', Icon: EnvelopeIcon },
};

const truncate = (value = '', max = 64) => {
  const text = String(value || '').trim();
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
};

const getInitials = (value = '') => {
  const tokens = String(value || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2);

  if (tokens.length === 0) return 'BB';
  return tokens.map((token) => token.charAt(0).toUpperCase()).join('');
};

const toMillis = (ts) => {
  if (!ts) return 0;
  if (typeof ts === 'number') return ts;
  if (ts.toDate) return ts.toDate().getTime();
  if (ts.seconds) return ts.seconds * 1000;
  const parsed = new Date(ts).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const AdminMessagingLauncher = () => {
  const [open, setOpen] = useState(false);
  const [portalConversations, setPortalConversations] = useState([]);
  const [externalConversations, setExternalConversations] = useState([]);
  const [loading, setLoading] = useState(false);
  const { sessionUser, firebaseUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;

    const loadUnreadConversations = async ({ silent = false } = {}) => {
      if (!silent) {
        setLoading(true);
      }

      try {
        const results = await listAdminUnreadConversations();
        if (cancelled) return;
        setPortalConversations(Array.isArray(results) ? results : []);
      } catch (error) {
        if (!cancelled) {
          console.warn('No se pudo cargar el launcher de mensajeria admin:', error);
        }
      } finally {
        if (!cancelled && !silent) {
          setLoading(false);
        }
      }
    };

    loadUnreadConversations();
    const intervalId = window.setInterval(() => loadUnreadConversations({ silent: true }), POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, []);

  // Mismo query que ya usa /admin/whatsapp (messaging_conversations,
  // updatedAt desc) — filtramos a "esperando respuesta nuestra" en el
  // cliente para no depender de un índice compuesto nuevo.
  useEffect(() => {
    const q = query(collection(db, 'messaging_conversations'), orderBy('updatedAt', 'desc'), limit(50));
    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs
          .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
          .filter((conv) => conv.waitingOn === 'agent');
        setExternalConversations(list);
      },
      (error) => console.warn('No se pudo cargar el inbox externo en el launcher admin:', error)
    );
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const handleToggle = () => {
      setOpen((prev) => !prev);
    };

    window.addEventListener('admin-messaging:toggle', handleToggle);
    return () => {
      window.removeEventListener('admin-messaging:toggle', handleToggle);
    };
  }, []);

  const mergedItems = useMemo(() => {
    const portalItems = portalConversations.map((item) => ({
      key: `portal_${item.clientId || item.clientEmail}`,
      source: 'portal',
      title: item.clientName || item.clientEmail || 'Cliente',
      subtitle: item.clientEmail || item.clientId || '',
      snippet: item.latestMessage?.body || 'Nuevo mensaje',
      ts: toMillis(item.latestMessage?.timestamp),
      badgeCount: Math.max(Number(item?.unreadCount || 1), 1),
      raw: item,
    }));

    const externalItems = externalConversations.map((conv) => ({
      key: `mc_${conv.id}`,
      source: conv.channel === 'messenger' ? 'messenger' : conv.channel === 'email' ? 'email' : 'whatsapp',
      title: conv.contactName || conv.phone || conv.leadName || 'Contacto',
      subtitle: conv.clientId ? 'Cliente' : 'Lead',
      snippet: conv.lastMessagePreview || 'Nuevo mensaje',
      ts: toMillis(conv.lastInboundAt || conv.updatedAt),
      badgeCount: 1,
      raw: conv,
    }));

    return [...portalItems, ...externalItems].sort((a, b) => b.ts - a.ts);
  }, [portalConversations, externalConversations]);

  const unreadCount = mergedItems.reduce((sum, item) => sum + item.badgeCount, 0);

  const launcherProfile = useMemo(() => {
    const displayName = sessionUser?.name
      || sessionUser?.displayName
      || firebaseUser?.displayName
      || sessionUser?.email
      || firebaseUser?.email
      || 'Mi perfil';
    const subtitle = sessionUser?.roleLabel
      || sessionUser?.department
      || sessionUser?.accessRole
      || firebaseUser?.email
      || 'Soporte interno';

    return {
      displayName,
      subtitle,
      initials: getInitials(displayName),
    };
  }, [firebaseUser, sessionUser]);

  const isOnClientMessagePage = location.pathname.includes('/admin/clients/') && location.pathname.endsWith('/email');
  // /admin/whatsapp ya ES el inbox completo — mostrar la burbuja encima solo
  // taparía sus propios controles de respuesta.
  const isOnWhatsAppWorkspace = location.pathname.startsWith('/admin/whatsapp');

  if (isOnClientMessagePage || isOnWhatsAppWorkspace) {
    return null;
  }

  const openItem = async (item) => {
    if (item.source === 'portal') {
      const targetId = item.raw?.clientId || item.raw?.clientEmail;
      if (!targetId) return;
      try {
        await markConversationMessagesRead({ clientId: targetId });
      } catch (error) {
        console.warn('No se pudieron marcar mensajes como leidos desde launcher admin:', error);
      }
      setOpen(false);
      navigate(`/admin/clients/${encodeURIComponent(targetId)}/email`);
      return;
    }

    setOpen(false);
    navigate('/admin/whatsapp', { state: { conversationId: item.raw.id } });
  };

  return (
    <>
      {open && (
        <div className="fixed inset-0 z-[70] bg-slate-950/20 backdrop-blur-[1px]" onClick={() => setOpen(false)} />
      )}

      {/* bottom-24 en móvil para no quedar tapado por la barra inferior nueva
          (fixed, md:hidden) — en PC (md:) no hay esa barra, así que baja a bottom-6. */}
      <div className="fixed bottom-24 right-4 z-[80] md:bottom-6 md:right-6">
        {open && (
          <div className="mb-3 w-[22rem] max-w-[calc(100vw-2rem)] overflow-hidden rounded-[1.75rem] border border-slate-200 bg-white shadow-[0_28px_80px_rgba(15,23,42,0.24)]">
            <div className="flex items-center justify-between bg-slate-950 px-4 py-4 text-white">
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/10 text-sm font-semibold text-white ring-1 ring-white/15">
                  {launcherProfile.initials}
                </div>
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-white">{launcherProfile.displayName}</p>
                  <p className="truncate text-[0.72rem] uppercase tracking-[0.18em] text-sky-200">
                    {launcherProfile.subtitle}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-full bg-white/10 p-2 text-white transition hover:bg-white/20"
                aria-label="Cerrar mensajeria"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="max-h-[26rem] overflow-y-auto bg-slate-50 p-3">
              {loading && mergedItems.length === 0 ? (
                <div className="rounded-2xl border border-slate-200 bg-white px-4 py-6 text-center text-sm text-slate-500">
                  Cargando conversaciones...
                </div>
              ) : mergedItems.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-6 text-center">
                  <p className="text-sm font-medium text-slate-700">No hay mensajes pendientes.</p>
                  <button
                    type="button"
                    onClick={() => {
                      setOpen(false);
                      navigate('/admin/clients');
                    }}
                    className="mt-3 inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-800"
                  >
                    Ir a clientes
                    <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  {mergedItems.map((item) => {
                    const meta = CHANNEL_META[item.source] || CHANNEL_META.portal;
                    const ChannelIcon = meta.Icon;
                    return (
                      <button
                        key={item.key}
                        type="button"
                        onClick={() => openItem(item)}
                        className="flex w-full items-start justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-left transition hover:border-sky-300 hover:bg-sky-50"
                      >
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[0.65rem] font-semibold ${meta.className}`}>
                              <ChannelIcon className="h-3 w-3" />
                              {meta.label}
                            </span>
                            <p className="truncate text-sm font-semibold text-slate-900">{item.title}</p>
                          </div>
                          {item.subtitle && <p className="mt-0.5 truncate text-xs text-slate-500">{item.subtitle}</p>}
                          <p className="mt-1 truncate text-sm text-slate-700">{truncate(item.snippet)}</p>
                        </div>
                        <div className="shrink-0 rounded-full bg-amber-400 px-2 py-1 text-xs font-bold text-slate-900">
                          {item.badgeCount}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          title={launcherProfile.displayName}
          aria-label="Mensajería"
          className="group relative flex h-14 w-14 items-center justify-center rounded-full bg-white p-1 shadow-[0_18px_45px_rgba(15,23,42,0.35)] ring-1 ring-slate-200 transition hover:scale-105"
        >
          <img src="/logo-bbts-round.png" alt="BBTS" className="h-full w-full rounded-full object-contain" />
          {unreadCount > 0 && (
            <span className="absolute -right-1 -top-1 flex min-h-[1.35rem] min-w-[1.35rem] items-center justify-center rounded-full border-2 border-white bg-amber-400 px-1 text-[0.68rem] font-bold text-slate-950">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      </div>
    </>
  );
};

export default AdminMessagingLauncher;
